import React, { createContext, useContext, useState, useCallback } from 'react';
import { weatherService } from '../services/weatherService';
import { aiService } from '../services/aiService';

const WeatherContext = createContext({});

export const useWeather = () => {
  const context = useContext(WeatherContext);
  if (!context) {
    throw new Error('useWeather must be used within a WeatherProvider');
  }
  return context;
};

export const WeatherProvider = ({ children }) => {
  const [weatherData, setWeatherData] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aiExplanation, setAiExplanation] = useState(null);

  // Fetch weather data for a location
  const fetchWeather = useCallback(async (lat, lon) => {
    setLoading(true);
    setError(null);

    try {
      const [current, forecastData] = await Promise.all([
        weatherService.getCurrentWeather(lat, lon),
        weatherService.getForecast(lat, lon),
      ]);

      setWeatherData(current);
      setForecast(forecastData);

      // Generate AI explanation
      const explanation = await aiService.generateWeatherExplanation(current, forecastData);
      setAiExplanation(explanation);

      return { current, forecast: forecastData };
    } catch (err) {
      setError(err.message);
      console.error('Error fetching weather:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Calculate risk score for a specific time window
  const calculateRiskScore = useCallback((forecastData, startTime, duration) => {
    if (!forecastData) return null;

    const endTime = new Date(startTime.getTime() + duration * 60 * 60 * 1000);
    const relevantForecasts = forecastData.list.filter((item) => {
      const itemTime = new Date(item.dt * 1000);
      return itemTime >= startTime && itemTime <= endTime;
    });

    if (relevantForecasts.length === 0) return null;

    // Calculate composite risk score (0-100)
    let totalRisk = 0;
    relevantForecasts.forEach((item) => {
      const precipProb = item.pop * 100; // Precipitation probability
      const windRisk = Math.min((item.wind.speed / 15) * 100, 100); // Wind speed risk
      const tempRisk = Math.abs(item.main.temp - 20) * 2; // Temperature comfort risk
      const humidityRisk = Math.abs(item.main.humidity - 50); // Humidity comfort risk

      // Weighted composite score
      const itemRisk = precipProb * 0.4 + windRisk * 0.25 + tempRisk * 0.2 + humidityRisk * 0.15;
      totalRisk += itemRisk;
    });

    return Math.round(totalRisk / relevantForecasts.length);
  }, []);

  // Find best time slots for an event
  const findBestTimeSlots = useCallback(
    async (lat, lon, date, duration, tolerances = {}) => {
      setLoading(true);
      setError(null);

      try {
        const forecastData = await weatherService.getForecast(lat, lon);
        const slots = [];

        // Check every hour of the day
        for (let hour = 6; hour < 22; hour++) {
          const startTime = new Date(date);
          startTime.setHours(hour, 0, 0, 0);

          const riskScore = calculateRiskScore(forecastData, startTime, duration);
          if (riskScore !== null) {
            const relevantForecasts = forecastData.list.filter((item) => {
              const itemTime = new Date(item.dt * 1000);
              const endTime = new Date(startTime.getTime() + duration * 60 * 60 * 1000);
              return itemTime >= startTime && itemTime <= endTime;
            });

            const avgPrecipProb = relevantForecasts.reduce((sum, f) => sum + f.pop, 0) / relevantForecasts.length;
            const avgWindSpeed = relevantForecasts.reduce((sum, f) => sum + f.wind.speed, 0) / relevantForecasts.length;

            slots.push({
              startTime,
              endTime: new Date(startTime.getTime() + duration * 60 * 60 * 1000),
              riskScore,
              precipitationProb: Math.round(avgPrecipProb * 100),
              windSpeed: Math.round(avgWindSpeed),
              temperature: Math.round(relevantForecasts[0].main.temp),
            });
          }
        }

        // Sort by risk score and return top 3
        const bestSlots = slots.sort((a, b) => a.riskScore - b.riskScore).slice(0, 3);

        // Generate AI suggestions
        const suggestions = await aiService.generateTimeSuggestions(bestSlots, duration);

        return { slots: bestSlots, suggestions };
      } catch (err) {
        setError(err.message);
        console.error('Error finding best time slots:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [calculateRiskScore]
  );

  const value = {
    weatherData,
    forecast,
    loading,
    error,
    aiExplanation,
    fetchWeather,
    calculateRiskScore,
    findBestTimeSlots,
  };

  return <WeatherContext.Provider value={value}>{children}</WeatherContext.Provider>;
};
